// interview.schema.json source — B4 (plan 20260915-1650 phase-04, spec §B4).
//
// Câu hỏi cho người, có ID ỔN ĐỊNH. Hôm nay escalation/clarify chỉ là văn xuôi
// trong post Mattermost (`workflows/autopilot/escalation.md` mục "Nội dung post
// escalation", `clarify-questions.md` 6 nhóm): người đọc hiểu được, nhưng KHÔNG
// máy nào biết "câu nào đã được trả lời" — nên không gate nào chặn được việc đi
// tiếp khi câu required còn treo.
//
// `type` quyết định câu trả lời hợp lệ là gì. `single`/`multiple` BẮT BUỘC có
// `options`: một câu chọn mà không có gì để chọn thì người trả lời phải đoán, và
// `recordInterviewAnswer` mất luôn căn cứ để từ chối giá trị bịa.
import { z } from "zod";

export const InterviewQuestionTypeSchema = z.enum(["text", "single", "multiple"]);

export const InterviewQuestionSchema = z
  .object({
    /** ID ổn định giữa các lần hỏi lại — người trả lời trích đúng id này. */
    id: z.string().min(1),
    title: z.string().min(1),
    type: InterviewQuestionTypeSchema,
    /** Bắt buộc với `single`/`multiple` (xem refine dưới), vô nghĩa với `text`. */
    options: z.array(z.string().min(1)).min(1).optional(),
    /**
     * V2-1 (spec V2 §4.1, E3): id của từng option, CÙNG thứ tự `options` (nhãn
     * hiển thị). Có field này ⇒ giá trị trả lời lưu là id, nên hai option trùng
     * nhãn khác id vẫn phân biệt được. Vắng ⇒ câu v1 (giá trị = nhãn). Chỉ ghi
     * khi worker gửi id hợp lệ (needs-decision-option-id-label-pairs.ts).
     */
    option_ids: z.array(z.string().min(1)).min(1).optional(),
    /**
     * `required: true` ⇒ chưa trả lời thì ticket phụ thuộc KHÔNG dispatch.
     * KHÔNG có đường nào tự suy câu trả lời từ default — đó là quyền của người
     * (phase-04 "KHÔNG làm": không auto-answer cho câu required).
     */
    required: z.boolean(),
    /** Hệ quả nếu bỏ qua — văn xuôi cho người đọc, không ràng buộc máy. */
    consequence: z.string().min(1).optional(),
  })
  .strict()
  .refine((q) => q.type === "text" || (q.options !== undefined && q.options.length > 0), {
    message: "câu hỏi type single/multiple phải khai options[] không rỗng",
    path: ["options"],
  })
  .refine(
    (q) =>
      q.option_ids === undefined ||
      (q.options?.length === q.option_ids.length &&
        new Set(q.option_ids).size === q.option_ids.length),
    { message: "option_ids phải duy nhất và cùng độ dài options[]", path: ["option_ids"] },
  );

export type InterviewQuestion = z.infer<typeof InterviewQuestionSchema>;

export const InterviewAnswerSchema = z
  .object({
    /** Giá trị người trả lời. `multiple` dùng mảng, còn lại dùng chuỗi. */
    value: z.union([z.string().min(1), z.array(z.string().min(1)).min(1)]),
    answered_at: z.string().min(1),
    /** actor_id của directive người; `delegated:<G0 id>` (máy tự áp); `chat_delegated:<os>` (06k: Claude thay chủ mission qua chat — không bao giờ ghi như người). */
    answered_by: z.string().min(1),
  })
  .strict();

export type InterviewAnswer = z.infer<typeof InterviewAnswerSchema>;

export const InterviewSchema = z
  .object({
    schema_version: z.literal(1),
    asked_at: z.string().min(1),
    questions: z.array(InterviewQuestionSchema),
    /** Khoá = `question.id`. Câu chưa trả lời đơn giản là không có khoá. */
    answers: z.record(z.string(), InterviewAnswerSchema),
  })
  .strict()
  .refine(
    (interview) =>
      new Set(interview.questions.map((q) => q.id)).size === interview.questions.length,
    { message: "id câu hỏi phải duy nhất — trùng id thì câu trả lời không trỏ đúng câu hỏi nào" },
  );

export type Interview = z.infer<typeof InterviewSchema>;
